<script setup lang="ts">
import BackstageImageV2 from '@/components/common/BackstageImageV2.vue';
import { ProductLabelEnum } from '@onrunning/backstage-models';
import { computed } from 'vue';

const props = defineProps<{
  imageSource?: string;
  labels: ProductLabelEnum[];
}>();

/**
 * Returns a thumbnail-sized URL for known CDNs, falling back to the original URL.
 * - library.on.com (Bynder): uses the `io` query parameter for server-side resize.
 *   Bynder serves AVIF by default, which is kept as-is for smaller file sizes.
 * - images.salsify.com: rewrites the width segment to 150px for a smaller thumbnail.
 *   Salsify serves WebP by default, which is kept as-is for smaller file sizes.
 */
function getThumbnailImageUrl(imageUrl: string): string {
  try {
    const url = new URL(imageUrl);

    if (url.hostname === 'library.on.com' && url.pathname.startsWith('/transform/')) {
      url.searchParams.set('io', 'transform:fill,width:150,height:150,gravity:center');

      return url.toString();
    }

    // Resize to 150px width to reduce file size (thumbnails only)
    if (url.hostname === 'images.salsify.com') {
      url.pathname = url.pathname.replace(/w_\d+/, 'w_150');

      return url.toString();
    }
  } catch {
    // Invalid URL — return as-is and let validation handle it
  }

  return imageUrl;
}

const isNew = computed(() => props.labels.includes(ProductLabelEnum.NEW));
const thumbnailSrc = computed(() => (props.imageSource ? getThumbnailImageUrl(props.imageSource) : ''));
</script>

<template>
  <div class="product-card">
    <div class="card-image-wrapper">
      <BackstageImageV2 :src="thumbnailSrc" alt="" eager class="card-image" />
      <img v-if="isNew" src="@/assets/img/icons/new_icon.svg" alt="" class="card-label-icon" />
    </div>
    <div class="card-text">
      <slot></slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.product-card {
  background-color: var(--white-color);
  padding: 10px;
  box-sizing: border-box;
  page-break-inside: avoid;
  display: flex;
  flex-direction: column;
  align-items: center;

  .card-image-wrapper {
    position: relative;
    width: 140px;
    height: 140px;

    .card-label-icon {
      position: absolute;
      top: 0;
      left: 0;
      width: 16px;
      height: 16px;
      background-color: var(--white-color);
    }
  }

  .card-text {
    align-self: flex-start;
    font-size: 0.5rem;
    color: var(--primary-dark);
    margin: 0;
    text-align: left;
    line-height: 1.4;
  }
}

.card-image-wrapper :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}
</style>
